import { ceilLdsPaymentRemainingAmount } from "../../utils/orders/ldsPaymentAmountRounding";

/** Shared ILS formatter for PackagePaymentView (mockup + live).
 * Chargeable ILS matches PaymentAgent/CardsProvider (ceilLdsPaymentRemainingAmount).
 */
export function formatPaymentIls(amount) {
  if (amount == null || !Number.isFinite(Number(amount))) return "—";
  const n = Number(amount);
  const display = n > 0 ? ceilLdsPaymentRemainingAmount(n) : n;
  return `₪${display.toLocaleString("he-IL")}`;
}

/** USD headline for payment summary (integer $, same family as dual-currency cards). */
export function formatPaymentUsd(amount) {
  if (amount == null || !Number.isFinite(Number(amount)) || Number(amount) <= 0) {
    return null;
  }
  const n = Math.round(Number(amount));
  try {
    return `$${n.toLocaleString("en-US")}`;
  } catch {
    return `$${String(n)}`;
  }
}

/**
 * Convert ILS → USD using ilsToUsd fn and/or currencyRate.USD (ils per 1 USD).
 * @returns {number|null}
 */
export function resolvePaymentUsdFromIls(ilsAmount, ilsToUsd, currencyRate) {
  const ils = Number(ilsAmount);
  if (!Number.isFinite(ils) || ils <= 0) return null;
  if (typeof ilsToUsd === "function") {
    const u = Number(ilsToUsd(ils, false));
    if (Number.isFinite(u) && u > 0) return u;
  }
  const rate = Number(currencyRate?.USD);
  if (Number.isFinite(rate) && rate > 0) return ils / rate;
  return null;
}

/**
 * ILS per 1 USD for payment summary display.
 * @param {number|null|undefined|{USD?: number}} rateOrCurrencyRate
 * @returns {number|null}
 */
export function resolvePaymentUsdIlsRate(rateOrCurrencyRate) {
  if (rateOrCurrencyRate == null) return null;
  if (typeof rateOrCurrencyRate === "object") {
    const n = Number(rateOrCurrencyRate.USD);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  const n = Number(rateOrCurrencyRate);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Label for payment-time FX.
 * Without a currency, keeps the legacy «3.108 ₪ = 1$» USD label.
 * With a currency, names that supplier currency: «1 USD = ₪3.1461».
 * @param {number|null|undefined|{USD?: number}} rateOrCurrencyRate
 * @param {string|null|undefined} [currency]
 * @returns {string|null}
 */
export function formatPaymentExchangeRate(rateOrCurrencyRate, currency) {
  const code = String(currency || "").trim().toUpperCase();
  const rate = code
    ? Number(rateOrCurrencyRate)
    : resolvePaymentUsdIlsRate(rateOrCurrencyRate);
  if (!Number.isFinite(rate) || rate <= 0 || code === "ILS") return null;
  const rounded = Math.round(rate * 10000) / 10000;
  const text = Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toLocaleString("he-IL", {
        minimumFractionDigits: 0,
        maximumFractionDigits: code ? 4 : 3,
      });
  if (code) return `1 ${code} = ₪${text}`;
  const legacy = Math.round(rate * 1000) / 1000;
  const legacyText = Number.isInteger(legacy)
    ? String(legacy)
    : legacy.toLocaleString("he-IL", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 3,
      });
  return `${legacyText} ₪ = 1$`;
}
